import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import BaliHolidayNote from "@/components/feature/BaliHolidayNote";
import DatePicker from "@/components/base/DatePicker";
import NationalitySelect from "@/components/base/NationalitySelect";
import { submitAdventureInquiry } from "@/lib/villaApi";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useTourBlockedDates, isTourRangeBlocked } from "@/hooks/useTourBlockedDates";
import { LocalizedLink } from "@/i18n/language";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };

type ActivityConfig = {
  activity: "rafting" | "sailing";
  eyebrowDe: string;
  eyebrowEn: string;
  titleDe: string;
  titleEn: string;
  subtitleDe: string;
  subtitleEn: string;
  image: string;
  imageAlt: string;
  activityLabelDe: string;
  activityLabelEn: string;
  price: number;
};

const ACTIVITY_CONFIGS: Record<"rafting" | "sailing", ActivityConfig> = {
  rafting: {
    activity: "rafting",
    eyebrowDe: "Erlebnistour",
    eyebrowEn: "Adventure Tour",
    titleDe: "Rafting buchen",
    titleEn: "Book Rafting",
    subtitleDe:
      "Reservieren Sie Ihr Rafting-Abenteuer auf dem Ayung Fluss. Wir melden uns so schnell wie möglich, in der Regel innerhalb von 24 Stunden, persönlich per E-Mail.",
    subtitleEn:
      "Reserve your rafting adventure on the Ayung River. We will get back to you as soon as possible, usually within 24 hours, by email.",
    image: img("activities/adventure-tours/adventure-1/header-adventure-1.jpg"),
    imageAlt: "Rafting auf dem Ayung Fluss",
    activityLabelDe: "Rafting auf dem Ayung Fluss",
    activityLabelEn: "Rafting on the Ayung River",
    price: 60,
  },
  sailing: {
    activity: "sailing",
    eyebrowDe: "Erlebnistour",
    eyebrowEn: "Adventure Tour",
    titleDe: "Segeltörn buchen",
    titleEn: "Book Sailing",
    subtitleDe:
      "Reservieren Sie Ihren Segeltörn zur Insel Lembongan. Wir melden uns so schnell wie möglich, in der Regel innerhalb von 24 Stunden, persönlich per E-Mail.",
    subtitleEn:
      "Reserve your sailing trip to Lembongan Island. We will get back to you as soon as possible, usually within 24 hours, by email.",
    image:
      img("activities/adventure-tours/adventure-2/header-sailing-01.jpg"),
    imageAlt: "Segeltörn zur Insel Lembongan",
    activityLabelDe: "Segeln zur Insel Lembongan",
    activityLabelEn: "Sailing to Lembongan Island",
    price: 140,
  },
};

export default function AdventureBookingForm({
  activity,
}: {
  activity: "rafting" | "sailing";
}) {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const config = ACTIVITY_CONFIGS[activity];
  const { priceMap, money } = usePriceMap();
  const tourBlockedDates = useTourBlockedDates();

  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [date, setDate] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = maxDate.toISOString().split("T")[0];

  const adultPrice = priceMap[config.activity] ?? config.price;
  const childPrice = adultPrice * 0.7;
  const totalPrice = adults * adultPrice + children * childPrice;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot =
      (form.elements.namedItem("phone_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: t("form_success") });
      form.reset();
      setAdults(1);
      setChildren(0);
      setDate("");
      return;
    }

    const fd = new FormData(form);
    fd.delete("phone_alt");

    const body: Record<string, unknown> = {};
    fd.forEach((value, key) => {
      if (typeof value === "string") body[key] = value;
    });

    const required: Array<[string, string]> = [
      ["date", isDe ? "Datum der Aktivität" : "Activity date"],
      ["hotel", isDe ? "Hotel / Abholung" : "Hotel / Pickup"],
      ["firstname", isDe ? "Vorname(n)" : "First name(s)"],
      ["lastname", isDe ? "Familienname(n)" : "Last name(s)"],
      ["nationality", isDe ? "Nationalität" : "Nationality"],
      ["email", isDe ? "E-Mail-Adresse" : "Email address"],
      ["phone", isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"],
    ];
    for (const [key, label] of required) {
      if (!String(body[key] ?? "").trim()) {
        setStatus({
          type: "error",
          msg: isDe
            ? `Bitte füllen Sie das Pflichtfeld „${label}" aus.`
            : `Please fill in the required field "${label}".`,
        });
        return;
      }
    }
    if (!body.terms || !body.privacy) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Bitte akzeptieren Sie die AGB und die Datenschutzerklärung."
          : "Please accept the terms and conditions and the privacy policy.",
      });
      return;
    }

    if (isTourRangeBlocked(tourBlockedDates, date, date)) {
      setStatus({
        type: "error",
        msg: isDe
          ? "An diesem Tag finden keine Touren statt. Bitte wählen Sie ein anderes Datum."
          : "No tours take place on this date. Please choose another date.",
      });
      return;
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { error } = await submitAdventureInquiry({
        activity: config.activity,
        date: String(body.date ?? ""),
        adults: String(body.adults ?? ""),
        children: String(body.children ?? ""),
        hotel: String(body.hotel ?? ""),
        operator: (body.operator as string) || null,
        lastname: String(body.lastname ?? "").toUpperCase(),
        firstname: String(body.firstname ?? ""),
        nationality: String(body.nationality ?? ""),
        email: String(body.email ?? ""),
        phone: (body.phone as string) || null,
        message: (body.message as string) || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        setStatus({ type: "error", msg: error.message || t("form_error") });
        return;
      }

      setStatus({ type: "success", msg: t("booking_form_success") });
      form.reset();
      setAdults(1);
      setChildren(0);
      setDate("");
    } catch {
      setStatus({ type: "error", msg: t("form_error") });
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors";

  const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";

  const sectionTitleClass =
    "font-heading text-xl text-foreground-950 mt-8 mb-4 pb-2 border-b border-background-200";

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? config.eyebrowDe : config.eyebrowEn}
          title={isDe ? config.titleDe : config.titleEn}
          subtitle={isDe ? config.subtitleDe : config.subtitleEn}
          image={config.image}
          imageAlt={config.imageAlt}
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                {/* ── Ihre Buchung ── */}
                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihre Buchung" : "Your Booking"}
                </h2>

                <div className="space-y-5">
                  {/* Aktivität (fix, nur Anzeige) */}
                  <div>
                    <label className={labelClass}>
                      {isDe ? "Aktivität" : "Activity"}
                    </label>
                    <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-950">
                      <i className="ri-flag-line text-accent-700"></i>
                      <span>
                        {isDe ? config.activityLabelDe : config.activityLabelEn}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-foreground-600">
                      {isDe
                        ? `Erwachsene: ${money(adultPrice)} · Kinder: ${money(childPrice)}`
                        : `Adults: ${money(adultPrice)} · Children: ${money(childPrice)}`}
                    </p>
                  </div>

                  {/* Datum */}
                  <div>
                    <label htmlFor="adventure_date" className={labelClass}>
                      {isDe ? "Datum der Aktivität" : "Activity date"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <DatePicker
                      name="date"
                      value={date}
                      min={today}
                      max={maxDateStr}
                      disabledDates={tourBlockedDates}
                      isDe={isDe}
                      placeholder={isDe ? "Datum wählen" : "Select date"}
                      onChange={setDate}
                    />
                    <BaliHolidayNote isDe={isDe} blockedDates={tourBlockedDates} />
                  </div>

                  {/* Anzahl Erwachsene */}
                  <div>
                    <label className={labelClass}>
                      {isDe ? "Anzahl Erwachsene" : "Number of adults"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.max(1, a - 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        name="adults"
                        type="number"
                        min={1}
                        max={50}
                        value={adults}
                        onChange={(e) =>
                          setAdults(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                        }
                        className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.min(50, a + 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Anzahl Kinder */}
                  <div>
                    <label className={labelClass}>
                      {isDe ? "Anzahl Kinder" : "Number of children"}
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setChildren((c) => Math.max(0, c - 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        name="children"
                        type="number"
                        min={0}
                        max={50}
                        value={children}
                        onChange={(e) =>
                          setChildren(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))
                        }
                        className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <button
                        type="button"
                        onClick={() => setChildren((c) => Math.min(50, c + 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Gesamtpreis */}
                  <div className="flex items-center justify-between bg-background-100 border border-background-200 rounded-lg px-5 py-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-accent-700"></i>
                      <span className="text-sm font-medium text-foreground-800">
                        {isDe ? "Gesamtpreis" : "Total price"}
                      </span>
                    </div>
                    <span className="font-heading text-lg font-semibold text-accent-700">
                      {money(totalPrice)}
                    </span>
                  </div>

                  {/* Hotel / Abholung */}
                  <div>
                    <label htmlFor="adventure_hotel" className={labelClass}>
                      {isDe ? "Hotel / Abholung in Bali" : "Hotel / Pickup in Bali"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="adventure_hotel"
                      name="hotel"
                      type="text"
                      required
                      placeholder={isDe ? "z. B. Villa Kompiang" : "e.g. Villa Kompiang"}
                      className={inputClass}
                    />
                  </div>

                  {/* Reiseveranstalter */}
                  <div>
                    <label htmlFor="adventure_operator" className={labelClass}>
                      {isDe ? "Reiseveranstalter (optional)" : "Tour operator (optional)"}
                    </label>
                    <input
                      id="adventure_operator"
                      name="operator"
                      type="text"
                      placeholder={isDe ? "z. B. TUI, Booking.com, etc." : "e.g. TUI, Booking.com, etc."}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* ── Ihre Angaben ── */}
                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihre Angaben" : "Your Details"}
                </h2>
                <p className="mb-5 -mt-2 text-sm text-foreground-600">
                  {isDe
                    ? <>Bitte bei zwei verschiedenen Namen immer beide angeben.<br />Max / Erika&nbsp;&nbsp;&nbsp;&nbsp;MUSTERMANN / MÜLLER</>
                    : <>If there are two different names, please always provide both.<br />John / Jane&nbsp;&nbsp;&nbsp;&nbsp;DOE / SMITH</>}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="adventure_firstname" className={labelClass}>
                      {isDe ? "Vorname(n)" : "First name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="adventure_firstname"
                      name="firstname"
                      type="text"
                      required
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="adventure_lastname" className={labelClass}>
                      {isDe ? "Familienname(n)" : "Last name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="adventure_lastname"
                      name="lastname"
                      type="text"
                      required
                      className={`${inputClass} uppercase`}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="adventure_nationality" className={labelClass}>
                    {isDe ? "Nationalität" : "Nationality"}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                  <NationalitySelect
                    id="adventure_nationality"
                    name="nationality"
                    isDe={isDe}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label htmlFor="adventure_email" className={labelClass}>
                      {isDe ? "E-Mail-Adresse" : "Email address"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="adventure_email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="sie@beispiel.de"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="adventure_phone" className={labelClass}>
                      {isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="adventure_phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+62 81 246 21 8000"
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Nachricht */}
                <div className="mt-5">
                  <label htmlFor="adventure_message" className={labelClass}>
                    {isDe ? "Ihre Nachricht / besondere Wünsche" : "Your message / special requests"}
                  </label>
                  <textarea
                    id="adventure_message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder={isDe
                      ? "Gibt es etwas, das wir wissen sollten? Ernährung, Abholzeit, besondere Wünsche..."
                      : "Is there anything we should know? Diet, pickup time, special requests..."}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-xs text-foreground-500 mt-1">
                    {isDe ? "Maximal 500 Zeichen" : "Maximum 500 characters"}
                  </p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  className="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {/* AGB Checkbox */}
                <div className="mt-6 flex items-start gap-3">
                  <input
                    id="adventure_terms"
                    name="terms"
                    type="checkbox"
                    value="1"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label
                    htmlFor="adventure_terms"
                    className="text-sm text-foreground-700 leading-relaxed cursor-pointer"
                  >
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          AGB
                        </LocalizedLink>{" "}
                        gelesen und akzeptiere sie.
                      </>
                    ) : (
                      <>
                        I have read and accept the{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          terms and conditions
                        </LocalizedLink>
                        .
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>

                {/* Datenschutz Checkbox */}
                <div className="mt-4 flex items-start gap-3">
                  <input
                    id="adventure_privacy"
                    name="privacy"
                    type="checkbox"
                    value="1"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label
                    htmlFor="adventure_privacy"
                    className="text-sm text-foreground-700 leading-relaxed cursor-pointer"
                  >
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          Datenschutzerklärung
                        </LocalizedLink>{" "}
                        gelesen und bin mit der Verarbeitung meiner Angaben zur Beantwortung der Anfrage einverstanden.
                      </>
                    ) : (
                      <>
                        I have read the{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          privacy policy
                        </LocalizedLink>{" "}
                        and agree to the processing of my data for the purpose of responding to my inquiry.
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>

                {/* Submit */}
                <div className="mt-8">
                  <div className="mb-5 bg-background-100 border border-background-200 rounded-lg px-5 py-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className="ri-group-line text-accent-700"></i>
                        <span className="text-sm font-medium text-foreground-800">
                          {isDe ? "Anzahl Personen" : "Number of persons"}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground-950">
                        {isDe
                          ? `${adults} Erwachsene${children > 0 ? `, ${children} Kinder` : ""}`
                          : `${adults} adults${children > 0 ? `, ${children} children` : ""}`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-background-200 pt-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-wallet-3-line text-accent-700"></i>
                        <span className="text-sm font-semibold text-foreground-950">
                          {isDe ? "Gesamtpreis" : "Total price"}
                        </span>
                      </div>
                      <span className="font-heading text-lg font-semibold text-accent-700">
                        {money(totalPrice)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={status.type === "sending"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-10 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {status.type === "sending" && (
                      <i className="ri-loader-4-line animate-spin"></i>
                    )}
                    {status.type === "sending"
                      ? (isDe ? "Wird gesendet..." : "Sending...")
                      : (isDe ? "Absenden" : "Submit")}
                  </button>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {/* Status messages */}
                {status.type === "success" && (
                  <div className="mt-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
                {status.type === "error" && (
                  <div className="mt-6 text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}